import { useEffect, useRef } from 'react';
import * as cognitoService from '../lib/auth/cognito.service';
import { useAuthStore } from '../stores/auth.store';
/**
 * Refresh threshold: when the tab becomes visible after being hidden for at
 * least this many ms, proactively refresh the Cognito session before any
 * TanStack Query refetch fires. Prevents the "user returns a day later, sees
 * a barrage of failed requests" scenario.
 */
const REFRESH_AFTER_HIDDEN_MS = 60 * 1000; // 1 minute
/**
 * Hook: when the tab/window regains visibility after being hidden for a while,
 * proactively refresh the auth session. Pairs with the proactive refresh in
 * `authenticatedFetch` to ensure that focus-triggered query refetches always
 * see a fresh token.
 *
 * Mounted once inside `ProtectedRoute` so it only runs while a user is
 * authenticated inside the app.
 */
export function useTokenRefreshOnFocus() {
    const { isAuthenticated } = useAuthStore();
    const hiddenSinceRef = useRef(null);
    useEffect(() => {
        if (!isAuthenticated)
            return;
        const onVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenSinceRef.current = Date.now();
                return;
            }
            if (document.visibilityState !== 'visible')
                return;
            const hiddenSince = hiddenSinceRef.current;
            hiddenSinceRef.current = null;
            const hiddenForMs = hiddenSince ? Date.now() - hiddenSince : 0;
            if (hiddenForMs < REFRESH_AFTER_HIDDEN_MS)
                return;
            // Fire and forget — getValidIdToken is single-flight, so any concurrent
            // API calls triggered by `refetchOnWindowFocus` will share this same
            // refresh promise. On hard failure, authenticatedFetch's own error path
            // will handle the redirect; we just want to warm the token here.
            cognitoService.getValidIdToken().catch((err) => {
                console.warn('Background token refresh on tab focus failed:', err);
            });
        };
        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [isAuthenticated]);
}
